// PRE RMA — Calcul LOCAL instantané des totaux (RCE + CPN), sans réseau, sans base de données.
//
// Principe : la saisie (tapée, collée ou importée) est TOUJOURS convertie par les mêmes fonctions pures que celles du serveur
// (parseAge → ageJours → calculerRmaDepuisLignes / calculerRmaCpn). L'écran, la file hors connexion, l'API et l'export Excel/PDF
// donnent donc les mêmes chiffres, quelle que soit la route d'entrée. Aucune de ces fonctions ne lève d'exception.
import { parseAge } from './age.ts';
import { normaliserConsultationInput } from './validation.ts';
import type { ConsultationInput } from './validation.ts';
import { calculerRmaDepuisLignes, avertissementsControle } from './rma.ts';
import type { ConsultationRow, RmaCalcule } from './rma.ts';
import { calculerRmaCpn, normaliserLigneCpn } from './cpn.ts';
import type { RmaCpnResultat } from './cpn.ts';
import { RULES } from './constants.ts';

/** Saisie → ligne de calcul. L'âge pivot (jours) vient UNIQUEMENT de parseAge ; un âge illisible donne null (et sera signalé, pas perdu en silence). */
export function ligneDepuisConsultation(saisie: Partial<Record<keyof ConsultationInput, unknown>> | null | undefined): ConsultationRow {
  const c = normaliserConsultationInput(saisie);
  const age = parseAge(c.ageBrut);
  return {
    numeroRceBrut: c.numeroRceBrut,
    casFievre: c.casFievre,
    tdrResultat: c.tdrResultat,
    ageJours: age.ok && age.ageJours !== undefined ? age.ageJours : null,
    genre: c.genre === 'M' || c.genre === 'F' ? c.genre : null,
    diagnostics: c.diagnostics,
    typeTraumatisme: c.typeTraumatisme,
    traitementPalu: c.traitementPalu,
    indicateurCrenas: c.indicateurCrenas,
    admissionCrenas: c.admissionCrenas,
  };
}

/** RMA complet (Parties 1-6, Tableau de suivi, contrôle) calculé sur place à partir des saisies. */
export function calculerRmaLocal(saisies: ReadonlyArray<Partial<Record<keyof ConsultationInput, unknown>> | null | undefined> | null | undefined): RmaCalcule {
  return calculerRmaDepuisLignes((Array.isArray(saisies) ? saisies : []).map(ligneDepuisConsultation));
}

/** Instantané compact des totaux, tel qu'il est rangé dans IndexedDB (petit, sérialisable, sans fonction). */
export interface TotauxLocaux {
  calculeLe: number;
  nbSaisies: number;
  totalCE: number;
  ceMoins5ans: number;
  ce5ansEtPlus: number;
  totalFievre: number;
  totalPaludisme: number;
  /** Paludisme (Partie 3) par tranche : 0-11 mois, 1-5 ans, 6-13 ans, 14-24 ans, 25 ans et plus. */
  palu: Array<{ tranche: string; casFievre: number; tdrEffectue: number; positifs: number; traitesACT: number; traitesArtesunate: number }>;
  /** Somme des 5 tranches palu = fièvre des lignes à âge exploitable ; contrôle croisé. */
  totalPaluTranches: number;
  avertissements: string[];
}

export function totauxLocaux(calc: RmaCalcule, maintenant: number = Date.now()): TotauxLocaux {
  const palu = calc.partie3.map((l) => ({
    tranche: l.tranche, casFievre: l.casFievre, tdrEffectue: l.tdrEffectue,
    positifs: l.positif101 + l.positif110 + l.positif111, traitesACT: l.traitesACT, traitesArtesunate: l.traitesArtesunate,
  }));
  return {
    calculeLe: maintenant,
    nbSaisies: calc.controle.total,
    totalCE: calc.tableauSuivi.totalCE,
    ceMoins5ans: calc.tableauSuivi.ceMoins5ans,
    ce5ansEtPlus: calc.tableauSuivi.ce5ansEtPlus,
    totalFievre: calc.tableauSuivi.totalFievre,
    totalPaludisme: calc.tableauSuivi.totalPaludisme,
    palu,
    totalPaluTranches: palu.reduce((a, l) => a + l.casFievre, 0),
    avertissements: avertissementsControle(calc.controle),
  };
}

/** Nombre de tranches paludisme attendues (5) : utilisé par les tests et l'écran pour vérifier qu'aucune n'a disparu. */
export const NB_TRANCHES_PALU = RULES.tranches.palu.length;

/** CPN : totaux locaux (même fonction que le serveur). */
export function calculerRmaCpnLocal(saisies: ReadonlyArray<{ ageAns?: unknown; indicateurs?: unknown } | null | undefined> | null | undefined): RmaCpnResultat {
  return calculerRmaCpn(saisies);
}

export interface TotauxCpnLocaux {
  calculeLe: number;
  nbSaisies: number;
  resultat: RmaCpnResultat;
  /** Lignes CPN dont l'âge est inutilisable (signalées, jamais ignorées en silence). */
  ageManquant: number;
}

export function totauxCpnLocaux(saisies: ReadonlyArray<{ ageAns?: unknown; indicateurs?: unknown } | null | undefined>, maintenant: number = Date.now()): TotauxCpnLocaux {
  const liste = Array.isArray(saisies) ? saisies : [];
  return {
    calculeLe: maintenant,
    nbSaisies: liste.length,
    resultat: calculerRmaCpn(liste),
    ageManquant: liste.filter((r) => normaliserLigneCpn(r).ageAns === null).length,
  };
}

// ---------------------------------------------------------------------
// CPN : fusion « serveur (ou cache) + file locale » — même principe que patients.ts pour le RCE.
// Sans elle, une femme saisie hors connexion n'apparaissait ni dans la liste, ni dans les totaux, et son N° de registre
// pouvait être proposé une 2e fois (doublon refusé plus tard par le serveur).
// ---------------------------------------------------------------------
import type { CpnIndicateur } from './cpn.ts';

export interface CpnServeurLigne {
  id: string; version: number; numeroRegistre: number; ageAns: number; indicateurs: Record<CpnIndicateur, boolean>;
  /** Champs ajoutés (migration 009) : date de prestation AAAA-MM-JJ, dépistage HTA (null = non renseigné), N° RCE lié. */
  datePrestation?: string | null; htaDepistage?: boolean | null; numeroRceLie?: string | null;
}
export interface CpnElementFile {
  localId: string; operation: 'CREATE' | 'UPDATE' | 'DELETE'; cpnId?: string; payload: Record<string, unknown>;
  syncStatus: 'PENDING' | 'SYNCED' | 'ERROR' | 'CONFLICT'; serverId?: string; serverVersion?: number; erreur?: string; createdAt: number;
}
export interface CpnAffichee {
  cle: string; id?: string; localId?: string; version?: number;
  numeroRegistre: number; ageAns: number; indicateurs: Record<CpnIndicateur, boolean>;
  datePrestation?: string | null; htaDepistage?: boolean | null; numeroRceLie?: string | null;
  enAttente: boolean; statut?: 'ATTENTE' | 'ERREUR' | 'CONFLIT'; message?: string;
}

const statutCpn = (s: CpnElementFile['syncStatus']): CpnAffichee['statut'] => (s === 'PENDING' ? 'ATTENTE' : s === 'ERROR' ? 'ERREUR' : s === 'CONFLICT' ? 'CONFLIT' : undefined);
const entier = (v: unknown): number => { const n = Number(v); return Number.isFinite(n) ? n : 0; };

export function fusionnerSaisiesCpn(serveur: ReadonlyArray<Partial<CpnServeurLigne>> | null | undefined, file: ReadonlyArray<CpnElementFile> | null | undefined): CpnAffichee[] {
  const parId = new Map<string, CpnAffichee>();
  const ordre: CpnAffichee[] = [];
  for (const s of Array.isArray(serveur) ? serveur : []) {
    if (!s || !s.id) continue;
    const l: CpnAffichee = { cle: s.id, id: s.id, version: s.version, numeroRegistre: entier(s.numeroRegistre), ageAns: entier(s.ageAns), indicateurs: normaliserLigneCpn(s).indicateurs, datePrestation: s.datePrestation ?? null, htaDepistage: s.htaDepistage ?? null, numeroRceLie: s.numeroRceLie ?? null, enAttente: false };
    parId.set(s.id, l); ordre.push(l);
  }
  const elements = [...(Array.isArray(file) ? file : [])].sort((a, b) => a.createdAt - b.createdAt);
  const depuisPayload = (p: Record<string, unknown>) => ({ numeroRegistre: entier(p.numeroRegistre), ageAns: entier(p.ageAns), indicateurs: normaliserLigneCpn({ indicateurs: p.indicateurs }).indicateurs,
    datePrestation: typeof p.datePrestation === 'string' ? p.datePrestation : null, htaDepistage: typeof p.htaDepistage === 'boolean' ? p.htaDepistage : null, numeroRceLie: typeof p.numeroRceLie === 'string' ? p.numeroRceLie : null });
  for (const it of elements) {
    if (it.operation !== 'CREATE') continue;
    if (it.syncStatus === 'SYNCED' && it.serverId) {
      const p = parId.get(it.serverId);
      if (p) p.localId = it.localId;
      else { const n: CpnAffichee = { cle: it.serverId, id: it.serverId, localId: it.localId, version: it.serverVersion, ...depuisPayload(it.payload), enAttente: false }; parId.set(it.serverId, n); ordre.push(n); }
    } else {
      ordre.push({ cle: it.localId, localId: it.localId, ...depuisPayload(it.payload), enAttente: true, statut: statutCpn(it.syncStatus), message: it.erreur });
    }
  }
  const masques = new Set<string>(); // saisies dont la suppression est en attente d'envoi : déjà supprimées pour l'utilisateur
  for (const it of elements) {
    if (it.operation === 'UPDATE' && it.syncStatus !== 'SYNCED' && it.cpnId) {
      const p = parId.get(it.cpnId);
      if (p) { Object.assign(p, depuisPayload(it.payload)); p.enAttente = true; p.statut = statutCpn(it.syncStatus); p.message = it.erreur; }
    } else if (it.operation === 'DELETE' && it.syncStatus !== 'SYNCED' && it.cpnId) {
      if (it.syncStatus === 'PENDING') masques.add(it.cpnId);
      else {
        // Refus / conflit du serveur : la saisie reste visible, avec le motif.
        const p = parId.get(it.cpnId);
        if (p) { p.enAttente = true; p.statut = statutCpn(it.syncStatus); p.message = it.erreur ?? 'Suppression non appliquée.'; }
      }
    }
  }
  return ordre.filter((l) => !(l.id && masques.has(l.id)));
}

/** Saisies CPN qui comptent dans les totaux : tout, sauf une création REFUSÉE/en conflit qui n'existe pas sur le serveur (elle est signalée à part). */
export function saisiesCpnComptees(l: ReadonlyArray<CpnAffichee>): CpnAffichee[] {
  return l.filter((x) => !(x.id === undefined && (x.statut === 'ERREUR' || x.statut === 'CONFLIT')));
}

/** Patients RCE qui comptent dans les totaux locaux : ceux du serveur + ceux en attente d'envoi ; une création REFUSÉE (jamais arrivée sur le serveur) est signalée à part. */
export function patientsComptes<P extends { id?: string; statut?: 'ATTENTE' | 'ERREUR' | 'CONFLIT' }>(patients: ReadonlyArray<P>): P[] {
  return patients.filter((p) => !(p.id === undefined && (p.statut === 'ERREUR' || p.statut === 'CONFLIT')));
}
